import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  FunnelIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ShoppingCartIcon,
  CurrencyDollarIcon,
  Squares2X2Icon,
  ListBulletIcon,
  XMarkIcon,
  StarIcon,
  TagIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { productsAPI } from '../utils/api';
import { Product } from '../types';
import QuoteRequestModal from '../components/QuoteRequestModal';

const ServiceGallery: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quoteProduct, setQuoteProduct] = useState<Product | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Amrod-style category structure
  const categories = [
    {
      id: 'all',
      name: 'All Categories',
      subcategories: []
    },
    {
      id: 'gifts',
      name: 'Corporate Gifts',
      subcategories: [
        'Executive Gifts',
        'Promotional Items',
        'Tech Accessories',
        'Stationery',
        'Drinkware',
        'Bags & Briefcases',
        'Home & Living',
        'Sports & Wellness',
        'Writing Instruments',
        'Personal Care'
      ]
    },
    {
      id: 'wear',
      name: 'Corporate Wear & Uniforms',
      subcategories: [
        'T-Shirts & Polos',
        'Jackets & Hoodies',
        'Workwear',
        'Formal Wear',
        'Outdoor Apparel',
        'Safety Wear',
        'Footwear',
        'Accessories'
      ]
    },
    {
      id: 'branding',
      name: 'Branding & Signage',
      subcategories: [
        'Logo Design',
        'Business Cards',
        'Stationery Sets',
        'Office Signage',
        'Vehicle Branding',
        'Window Graphics',
        'Interior Signage',
        'Brand Guidelines'
      ]
    },
    {
      id: 'printing',
      name: 'Printing & Display',
      subcategories: [
        'Banners & Flags',
        'Brochures & Flyers',
        'Large Format Printing',
        'Display Stands',
        'Trade Show Materials',
        'Posters & Posters',
        'Business Cards',
        'Stickers & Labels'
      ]
    },
    {
      id: 'collections',
      name: 'Collections & Themes',
      subcategories: [
        'Executive Collection',
        'Tech Collection',
        'Outdoor Collection',
        'Wellness Collection',
        'Eco-Friendly',
        'Premium Gifts',
        'Budget Options',
        'Seasonal Items'
      ]
    }
  ];

  // Fetch products from API
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productsAPI.getProducts({
        page: 1,
        limit: 100 // Get all products for now
      });
      
      if (response.data.success && response.data.data) {
        setProducts(response.data.data.products || []);
      } else {
        // Fallback to sample data if API fails
        setProducts(getSampleProducts());
      }
    } catch (err: any) {
      console.error('Failed to fetch products:', err);
      setError(err.response?.data?.message || 'Failed to fetch products');
      // Fallback to sample data if API fails
      setProducts(getSampleProducts());
    } finally {
      setLoading(false);
    }
  };

  // Sample products as fallback
  const getSampleProducts = (): Product[] => [
    {
      _id: '1',
      name: 'Executive Leather Portfolio',
      category: 'gifts',
      subcategory: 'Executive Gifts',
      description: 'Premium leather portfolio with custom embossing, perfect for business meetings and presentations.',
      price: 450,
      originalPrice: 550,
      images: ['https://via.placeholder.com/300x300/171344/F16A24?text=Leather+Portfolio'],
      stock: 10,
      customizable: true,
      featured: true,
      status: 'active',
      tags: ['Executive', 'Leather', 'Custom Embossing'],
      brand: 'Premium Collection',
      colors: ['Black', 'Brown', 'Tan'],
      inStock: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      _id: '2',
      name: 'Custom Branded USB Drive',
      category: 'gifts',
      subcategory: 'Tech Accessories',
      description: 'High-quality USB drive with your logo laser engraved. Available in 16GB, 32GB, and 64GB.',
      price: 85,
      images: ['https://via.placeholder.com/300x300/F16A24/171344?text=USB+Drive'],
      stock: 50,
      customizable: true,
      featured: true,
      status: 'active',
      tags: ['Tech', 'USB', 'Laser Engraved'],
      brand: 'Tech Essentials',
      colors: ['Silver', 'Black', 'Gold'],
      inStock: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      _id: '3',
      name: 'Embroidered Polo Shirt',
      category: 'wear',
      subcategory: 'T-Shirts & Polos',
      description: 'High-quality polo shirt with professional embroidery. Available in multiple colors and sizes.',
      price: 180,
      images: ['https://via.placeholder.com/300x300/F16A24/171344?text=Polo+Shirt'],
      stock: 25,
      customizable: true,
      featured: true,
      status: 'active',
      tags: ['Polo', 'Embroidered', 'Professional'],
      brand: 'Corporate Wear',
      colors: ['White', 'Navy', 'Red', 'Green'],
      sizes: ['S', 'M', 'L', 'XL', 'XXL'],
      inStock: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      _id: '4',
      name: 'Business Card Design',
      category: 'branding',
      subcategory: 'Business Cards',
      description: 'Professional business card design and printing service. Premium cardstock with spot UV coating.',
      price: 120,
      images: ['https://via.placeholder.com/300x300/F16A24/171344?text=Business+Cards'],
      stock: 100,
      customizable: true,
      featured: true,
      status: 'active',
      tags: ['Business Cards', 'Design', 'Premium'],
      brand: 'Branding Solutions',
      inStock: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      _id: '5',
      name: 'Roll-up Banner',
      category: 'printing',
      subcategory: 'Banners & Flags',
      description: 'Professional roll-up banner with custom design and printing. Includes carrying case.',
      price: 280,
      images: ['https://via.placeholder.com/300x300/F16A24/171344?text=Roll-up+Banner'],
      stock: 15,
      customizable: true,
      featured: true,
      status: 'active',
      tags: ['Banner', 'Roll-up', 'Professional'],
      brand: 'Display Solutions',
      inStock: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  ];

  // Filter and sort products
  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSubcategory = !selectedSubcategory || (product.subcategory || '') === selectedSubcategory;
    const matchesSearch = !searchTerm || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.tags || []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSubcategory && matchesSearch && product.status === 'active';
  }).sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'name':
        return a.name.localeCompare(b.name);
      case 'featured':
      default:
        return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
    }
  });

  const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold mb-2">Our Products</h1>
              <p className="text-gray-200 text-lg">
                Discover our comprehensive range of corporate gifts, wear, branding, and promotional solutions
              </p>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <div className="text-right">
                <div className="text-2xl font-bold">{filteredProducts.length}</div>
                <div className="text-sm text-gray-200">Products</div>
              </div>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-accent"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FunnelIcon className="h-5 w-5 mr-2" />
                Filters
              </button>
              <div className="flex border border-white/20 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-3 ${viewMode === 'grid' ? 'bg-accent' : 'hover:bg-white/10'}`}
                >
                  <Squares2X2Icon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-3 ${viewMode === 'list' ? 'bg-accent' : 'hover:bg-white/10'}`}
                >
                  <ListBulletIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar - Amrod Style */}
          <div className={`${showFilters ? 'block' : 'hidden'} lg:block w-64 flex-shrink-0`}>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-8">
              {/* Categories */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
                <div className="space-y-2">
                  {categories.map(category => (
                    <div key={category.id}>
                      <button
                        onClick={() => {
                          setSelectedCategory(category.id);
                          setSelectedSubcategory('');
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          selectedCategory === category.id
                            ? 'bg-primary text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {category.name}
                      </button>
                      
                      {/* Subcategories */}
                      {selectedCategory === category.id && category.subcategories.length > 0 && (
                        <div className="ml-4 mt-2 space-y-1">
                          {category.subcategories.map(subcategory => (
                            <button
                              key={subcategory}
                              onClick={() => setSelectedSubcategory(
                                selectedSubcategory === subcategory ? '' : subcategory
                              )}
                              className={`w-full text-left px-3 py-1 rounded text-sm transition-colors ${
                                selectedSubcategory === subcategory
                                  ? 'bg-accent text-white'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {subcategory}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Sort By</h3>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="featured">Featured</option>
                  <option value="name">Name A-Z</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Breadcrumb */}
            <div className="mb-6">
              <nav className="flex items-center space-x-2 text-sm text-gray-600">
                <Link to="/" className="hover:text-primary">Home</Link>
                <span>/</span>
                <span className="text-gray-900">Products</span>
                {selectedCategory !== 'all' && (
                  <>
                    <span>/</span>
                    <span className="text-gray-900">{selectedCategoryData?.name}</span>
                  </>
                )}
                {selectedSubcategory && (
                  <>
                    <span>/</span>
                    <span className="text-gray-900">{selectedSubcategory}</span>
                  </>
                )}
              </nav>
            </div>

            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedCategoryData?.name}
                  {selectedSubcategory && ` - ${selectedSubcategory}`}
                </h2>
                <p className="text-gray-600">
                  {filteredProducts.length} products found
                </p>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-yellow-800 text-sm">
                  {error} - Showing sample products for demonstration.
                </p>
              </div>
            )}

            {/* Products Grid */}
            {filteredProducts.length > 0 ? (
              <div className={`grid gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                  : 'grid-cols-1'
              }`}>
                {filteredProducts.map(product => (
                  <div
                    key={product._id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="relative">
                      <img
                        src={product.images?.[0] || '/api/placeholder/300/300'}
                        alt={product.name}
                        className="w-full h-48 object-cover"
                      />
                      {product.featured && (
                        <div className="absolute top-2 left-2 bg-accent text-white px-2 py-1 rounded text-xs font-semibold">
                          Featured
                        </div>
                      )}
                      {product.originalPrice && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold">
                          Sale
                        </div>
                      )}
                    </div>
                    
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-accent font-medium uppercase">
                          {product.category}
                        </span>
                        {product.brand && (
                          <span className="text-xs text-gray-500">{product.brand}</span>
                        )}
                      </div>
                      
                      <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                        {product.name}
                      </h3>
                      
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {product.description}
                      </p>
                      
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">
                            Custom Pricing
                          </span>
                        </div>
                        <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                          Customizable
                        </span>
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedProduct(product)}
                          className="flex-1 bg-primary text-white hover:bg-primary-dark px-3 py-2 rounded text-sm font-medium transition-colors"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => setQuoteProduct(product)}
                          className="px-3 py-2 border border-primary text-primary hover:bg-primary hover:text-white rounded text-sm font-medium transition-colors"
                        >
                          <ChatBubbleLeftRightIcon className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {(product.tags || []).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {(product.tags || []).slice(0, 3).map(tag => (
                            <span
                              key={tag}
                              className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <MagnifyingGlassIcon className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your search or filter criteria
                </p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                    setSelectedSubcategory('');
                  }}
                  className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex">
              <div className="w-1/2 p-6">
                <img
                  src={selectedProduct.images?.[0] || '/api/placeholder/400/400'}
                  alt={selectedProduct.name}
                  className="w-full h-96 object-cover rounded-lg"
                />
              </div>
              <div className="w-1/2 p-6">
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
                
                <div className="mb-4">
                  <span className="text-sm text-accent font-medium uppercase">
                    {selectedProduct.category} / {selectedProduct.subcategory || 'General'}
                  </span>
                  <h2 className="text-2xl font-bold text-gray-900 mt-2">
                    {selectedProduct.name}
                  </h2>
                  {selectedProduct.brand && (
                    <p className="text-sm text-gray-600 mt-1">by {selectedProduct.brand}</p>
                  )}
                </div>
                
                <p className="text-gray-700 mb-6">{selectedProduct.description}</p>
                
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-gradient-to-r from-primary to-primary-dark text-white px-4 py-2 rounded-lg">
                    <span className="text-sm font-medium">Custom Pricing Available</span>
                  </div>
                  <span className="px-3 py-1 rounded text-sm bg-blue-100 text-blue-800">
                    Fully Customizable
                  </span>
                </div>
                
                {selectedProduct.colors && selectedProduct.colors.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Colors:</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProduct.colors.map(color => (
                        <span
                          key={color}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                        >
                          {color}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedProduct.sizes && selectedProduct.sizes.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Sizes:</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProduct.sizes.map(size => (
                        <span
                          key={size}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                        >
                          {size}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex gap-4">
                  <button 
                    onClick={() => {
                      setSelectedProduct(null);
                      setQuoteProduct(selectedProduct);
                    }}
                    className="flex-1 bg-primary text-white hover:bg-primary-dark px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Request Custom Quote
                  </button>
                  <button 
                    onClick={() => setSelectedProduct(null)}
                    className="flex-1 border border-primary text-primary hover:bg-primary hover:text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
                
                {(selectedProduct.tags || []).length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-2">Tags:</h4>
                    <div className="flex flex-wrap gap-2">
                      {(selectedProduct.tags || []).map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quote Request Modal */}
      <QuoteRequestModal
        isOpen={!!quoteProduct}
        onClose={() => setQuoteProduct(null)}
        product={quoteProduct}
      />
    </div>
  );
};

export default ServiceGallery;